import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { createResourceRole, type CreateRolePayload } from '../lib/resourceRoles';

interface CreateRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRoleCreated: () => void;
  businessId: string;
}

export default function CreateRoleModal({ 
  isOpen, 
  onClose, 
  onRoleCreated, 
  businessId 
}: CreateRoleModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Role name is required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload: CreateRolePayload = {
        name: name.trim(),
        description: description.trim(),
        business_id: businessId
      };

      await createResourceRole(payload);
      
      // Reset form
      setName('');
      setDescription('');
      
      // Notify parent component
      onRoleCreated();
      onClose();
    } catch (err: any) {
      console.error('Error creating role:', err);
      if (err.message?.includes('unique_role_name_per_business')) {
        setError('A role with this name already exists');
      } else {
        setError('Failed to create role. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setName('');
      setDescription('');
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-[999999] p-2 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-2xl w-full max-w-md sm:mx-4 shadow-xl border border-gray-200">
        <div className="flex items-center justify-between border-b border-gray-200 px-3 sm:px-6 py-3 sm:py-5 bg-gray-50 rounded-t-xl sm:rounded-t-2xl">
          <h3 className="text-base sm:text-xl font-bold text-gray-900">Create New Role</h3>
          <button 
            onClick={handleClose}
            disabled={isLoading}
            className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95 disabled:opacity-50"
          >
            <X className="w-4 h-4 sm:w-4 sm:h-4" />
          </button>
        </div>

        <form 
          onSubmit={handleSubmit}
          className="px-3 sm:px-6 py-4 sm:py-4"
        >
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm mb-4">
              {error}
            </div>
          )}

          <div className="mb-4 sm:mb-4">
            <label className="block text-sm sm:text-sm font-semibold sm:font-medium text-gray-700 mb-2 sm:mb-2">
              Role Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 sm:py-2 border border-gray-300 rounded-lg sm:rounded-md text-sm sm:text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm disabled:opacity-50 disabled:bg-gray-50"
              placeholder="e.g., Manager, Technician, Administrator"
              maxLength={50}
              disabled={isLoading}
              required
              autoFocus
            />
          </div>

          <div className="mb-4 sm:mb-4">
            <label className="block text-sm sm:text-sm font-semibold sm:font-medium text-gray-700 mb-2 sm:mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 sm:py-2 border border-gray-300 rounded-lg sm:rounded-md text-sm sm:text-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 shadow-sm disabled:opacity-50 disabled:bg-gray-50 resize-none"
              placeholder="Brief description of this role's responsibilities..."
              rows={3}
              maxLength={200}
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4 sm:pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="w-full sm:w-auto px-3 py-2.5 sm:py-2 text-sm sm:text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg sm:rounded-md hover:bg-gray-50 transition-all duration-200 active:scale-95 shadow-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name.trim()}
              className="w-full sm:w-auto px-3 py-2.5 sm:py-2 text-sm sm:text-sm font-semibold text-white bg-purple-700 rounded-lg sm:rounded-md hover:bg-purple-600 transition-all duration-200 active:scale-95 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating...' : 'Create Role'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
} 